import { Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { timeout } from 'rxjs';
import { ReviewerService, IssueDetailsFilters } from '../core/services/reviewer.service';
import { UserRoleService } from '../core/services/user-role.service';
import { RebarAuthService } from '../core/rebarauth/rebar.auth.service';
import { CreateNewIssueComponent } from '../review-process/issue-management/create-new-issue/create-new-issue.component';
import { DateFormatPipe } from '../core/pipes/date-format.pipe';
import { ExcelExportService } from '../core/services/excel-export.service';

interface ColumnItem {
  key: string;
  label: string;
  visible: boolean;
}

interface ColumnFilter {
  op1: string;
  val1: string;
}

@Component({
  standalone: true,
  selector: 'app-issue-management',
  imports: [MatIconModule, CommonModule, FormsModule, CreateNewIssueComponent, DateFormatPipe],
  templateUrl: './issue-management.component.html',
  styleUrl: './issue-management.component.css',
})
export class IssueManagementComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private excelExportSvc = inject(ExcelExportService);

  pageSize = 10;
  currentPage = 1;
  goToPageInput = 1;
  sortColumn = 'identificationDate';
  sortDirection: 'asc' | 'desc' = 'desc';
  isFilterOpen = false;
  isLoading = false;
  isExporting = false;
  successToastMessage: string | null = null;
  searchText = '';
  hasDashboardFilter = false;

  activeFilterColumn: string | null = null;
  operators = ['Contains', 'Not Contains', 'Equals', 'Starts With', 'Ends With'];

  columnSearchText = '';
  data: any[] = [];
  private defaultOrder: any[] = [];

  basicExpanded = false;
  scheduleExpanded = false;
  reviewCycleExpanded = false;
  issueManagementExpanded = false;

  // ── Edit Issue Modal ──
  addIssueModalOpen = false;
  editingIssueData: any = null;
  editingIssueDocuments: any[] = [];
  editingReviewId: number | null = null;
  editingReviewTypeId: number = 1;
  editingTypeOfReview: string = '';
  nextIssueId = '';
  nextActionPlanId = '';
  actionPlans: any[] = [];
  serviceLines: string[] = [];
  repLetterQuestions: string[] = [];
  riskCategories: any[] = [];
  locations: string[] = [];
  internalDependencies: string[] = [];
  reviewRcReviewer = '';
  reviewRcSupervisor = '';
  reviewRcManager = '';
  reviewSecondaryRcReviewer = '';
  reviewSecondaryRcSupervisor = '';
  reviewSecondaryRcManager = '';
  financialYear: string | null = null;
  quarter: string | null = null;

  showDeleteModal = false;
  isDeletingIssue = false;
  private pendingDeleteItem: any = null;

  openDeleteModal(item: any): void {
    if (!this.isAdmin) return;
    this.pendingDeleteItem = item;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.pendingDeleteItem = null;
  }

  closeSuccessToast(): void {
    this.successToastMessage = null;
    document.body.style.overflow = '';
  }

  confirmDeleteIssue(): void {
    if (!this.pendingDeleteItem || this.isDeletingIssue) return;
    this.showDeleteModal = false;
    this.isDeletingIssue = true;
    const item = this.pendingDeleteItem;
    const loggedUserId = this.userRoleService.enterpriseId || (this.userRoleService.email ?? '').split('@')[0];
    const issueId = Number(String(item.issueId).replace(/^[A-Za-z]+-/i, '').trim());
    const reviewId = Number(item.reviewId);
    this.reviewerService.deleteIssue(reviewId, issueId, loggedUserId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.data = this.data.filter(d => d !== item);
          this.defaultOrder = this.defaultOrder.filter(d => d !== item);
          this.isDeletingIssue = false;
          this.pendingDeleteItem = null;
          this.successToastMessage = 'Issue deleted successfully';
          document.body.style.overflow = 'hidden';
          setTimeout(() => { this.successToastMessage = null; document.body.style.overflow = ''; }, 5000);
        },
        error: (err) => {
          console.error('Delete issue error:', err);
          this.isDeletingIssue = false;
          this.pendingDeleteItem = null;
        },
      });
  }

  private issueSubscriberCache = new Map<string, string[]>();
  private defaultVisibleKeys!: Set<string>;
  private readonly columnVisibilityStateVersion = 1;

  filterableColumns: string[] = [
    'reviewName', 'currentStatus', 'proposedYear', 'masterClientName', 'dntProjectName',
    'contractNumber', 'additionalContractNumber', 'marketUnit', 'customerRelationshipName',
    'planName', 'qadRecommendation', 'clientQualityCQI', 'clientAccountLead', 'masterClientID',
    'deliveryCountry', 'locationsToBeReviewed', 'isPrimaryLocation', 'quarter', 'proposedMonth',
    'rcManager', 'rcSupervisor', 'rcReviewer', 'secondaryManager', 'secondarySupervisor', 'secondaryReviewer',
    'accentureOperationsSL', 'targetForReportPublications',
    'actualPublicationDate', 'overallReportingRating',
    'issueId', 'issueTitle', 'issueDescription', 'imRcReviewer', 'imRcSupervisor', 'imRcManager',
    'identificationDate', 'subscribersNotify', 'rcComments', 'actionRequiredBy',
    'issueRating', 'issueStatus', 'locationReviewedImpacted', 'riskStatementL3', 'linkedActionPlan',
    'reviewObjective', 'recommendation', 'broadIssueCategory', 'detailedIssueCategory',
    'repLetterConflict', 'repLetterQuestionsWithConflict', 'serviceLine',
  ];

  filters: { [key: string]: ColumnFilter } = {};

  reviewBasicColumns: ColumnItem[] = [
    { key: 'reviewName', label: 'Review Name', visible: false },
    { key: 'currentStatus', label: 'Current Status', visible: false },
    { key: 'proposedYear', label: 'Proposed Year', visible: false },
    { key: 'masterClientName', label: 'Master Client Name', visible: false },
    { key: 'dntProjectName', label: 'D&T Project Name', visible: false },
    { key: 'contractNumber', label: 'Contract Number', visible: false },
    { key: 'additionalContractNumber', label: 'Additional Contract Number', visible: false },
    { key: 'marketUnit', label: 'Market Unit', visible: false },
    { key: 'customerRelationshipName', label: 'Customer Relationship Name', visible: false },
    { key: 'planName', label: 'Plan Name', visible: false },
    { key: 'qadRecommendation', label: 'QAD Recommendation (Opp) / QAD Overall Status (Del)', visible: false },
    { key: 'clientQualityCQI', label: 'Client Quality (CQI)', visible: false },
    { key: 'clientAccountLead', label: 'Client Account Lead (GR)', visible: false },
    { key: 'masterClientID', label: 'Master Client ID', visible: false },
    { key: 'deliveryCountry', label: 'Delivery Country*', visible: false },
    { key: 'locationsToBeReviewed', label: 'Locations to be reviewed*', visible: false },
    { key: 'isPrimaryLocation', label: 'Is it Primary Location (TBD)?', visible: false },
    { key: 'quarter', label: 'Quarter', visible: false },
    { key: 'proposedMonth', label: 'Proposed Month', visible: false },
    { key: 'rcManager', label: 'R&C Manager', visible: false },
    { key: 'rcSupervisor', label: 'R&C Supervisor', visible: false },
    { key: 'rcReviewer', label: 'R&C Reviewer', visible: false },
    { key: 'secondaryManager', label: 'Secondary Manager', visible: false },
    { key: 'secondarySupervisor', label: 'Secondary Supervisor', visible: false },
    { key: 'secondaryReviewer', label: 'Secondary Reviewer', visible: false },
  ];

  scheduleColumns: ColumnItem[] = [
    { key: 'accentureOperationsSL', label: 'Accenture Operation Service Line', visible: false },
    { key: 'targetForReportPublications', label: 'Target for Report Publications', visible: false },
  ];

  reviewCycleColumns: ColumnItem[] = [
    { key: 'actualPublicationDate', label: 'Actual Publication Date', visible: false },
    { key: 'overallReportingRating', label: 'Overall Reporting Rating', visible: false },
  ];

  issueManagementColumns: ColumnItem[] = [
    { key: 'issueId', label: 'Issue ID', visible: true },
    { key: 'issueTitle', label: 'Issue Title', visible: true },
    { key: 'issueDescription', label: 'Issue Description', visible: true },
    { key: 'imRcReviewer', label: 'R&C Reviewer', visible: false },
    { key: 'imRcSupervisor', label: 'R&C Supervisor', visible: false },
    { key: 'imRcManager', label: 'R&C Manager', visible: false },
    { key: 'identificationDate', label: 'Identification Date', visible: true },
    { key: 'subscribersNotify', label: 'Subscribers/Notify', visible: false },
    { key: 'rcComments', label: 'R&C Comments', visible: false },
    { key: 'actionRequiredBy', label: 'Action Required by', visible: false },
    { key: 'issueRating', label: 'Issue Rating', visible: true },
    { key: 'issueStatus', label: 'Issue Status', visible: true },
    { key: 'locationReviewedImpacted', label: 'Location Reviewed and Impacted', visible: true },
    { key: 'riskStatementL3', label: 'Risk Statement (L3)', visible: true },
    { key: 'linkedActionPlan', label: 'Linked Action Plan', visible: true },
    { key: 'reviewObjective', label: 'Review Objective', visible: false },
    { key: 'recommendation', label: 'Recommendation', visible: false },
    { key: 'broadIssueCategory', label: 'Broad Issue Category (L1)', visible: false },
    { key: 'detailedIssueCategory', label: 'Detailed Issue Category (L2)', visible: false },
    { key: 'repLetterConflict', label: 'RepLetter Conflict', visible: false },
    { key: 'repLetterQuestionsWithConflict', label: 'RepLetter Questions with Conflict', visible: false },
    { key: 'serviceLine', label: 'Accenture Operation Service Line', visible: false },
  ];

  get isReviewer(): boolean {
    return this.userRoleService.isReviewer;
  }

  get isAdmin(): boolean {
    return this.userRoleService.isAdmin;
  }

  get isSupervisor(): boolean {
    return this.userRoleService.isSupervisor;
  }

  get isRandCMember(): boolean {
    return this.userRoleService.isRCMember;
  }

  isAssignedToRow(item: any): boolean {
    const email = (this.userRoleService.email ?? '').toLowerCase();
    const enterpriseId = (this.userRoleService.enterpriseId ?? '').toLowerCase();
    if (!email && !enterpriseId) return false;
    const eid = enterpriseId || (email ? email.split('@')[0] : '');
    const fields = [
      item.rcManager, item.secondaryManager,
      item.rcSupervisor, item.secondarySupervisor,
      item.rcReviewer, item.secondaryReviewer,
    ];
    return fields.some(v => {
      if (!v) return false;
      return String(v).toLowerCase().split('@')[0] === eid;
    });
  }

  private isSupervisorOrManagerForRow(item: any): boolean {
    const email = (this.userRoleService.email ?? '').toLowerCase();
    const enterpriseId = (this.userRoleService.enterpriseId ?? '').toLowerCase();
    if (!email && !enterpriseId) return false;
    const eid = enterpriseId || (email ? email.split('@')[0] : '');
    const fields = [
      item.rcSupervisor, item.secondarySupervisor,
      item.imRcSupervisor,
      item.rcManager, item.secondaryManager,
      item.imRcManager,
    ];
    return fields.some(v => v && String(v).toLowerCase().split('@')[0] === eid);
  }

  constructor(private reviewerService: ReviewerService, private router: Router, private route: ActivatedRoute, private rebarAuth: RebarAuthService, private userRoleService: UserRoleService) {
    this.filterableColumns.forEach(c => {
      this.filters[c] = { op1: 'Contains', val1: '' };
    });
    this.defaultVisibleKeys = new Set([
      ...this.reviewBasicColumns.filter(c => c.visible).map(c => c.key),
      ...this.scheduleColumns.filter(c => c.visible).map(c => c.key),
      ...this.reviewCycleColumns.filter(c => c.visible).map(c => c.key),
      ...this.issueManagementColumns.filter(c => c.visible).map(c => c.key),
    ]);
    this.restoreFilterState();
  }

  ngOnInit() {
    const issueUpdatedHandler = (e: StorageEvent) => {
      if (e.key === 'ast_issue_updated') this.clearDashboardFilter();
    };
    window.addEventListener('storage', issueUpdatedHandler);
    this.destroyRef.onDestroy(() => window.removeEventListener('storage', issueUpdatedHandler));

    this.isLoading = true;
    const navState = this.router.getCurrentNavigation()?.extras?.state ?? history.state;
    let rawDashboardFilters = navState?.['issueDetailsFilters'];
    if (!rawDashboardFilters) {
      const filterToken = this.route.snapshot.queryParamMap.get('dashboardFilterToken');
      const storageKey = filterToken
        ? `pending_issueDetailsFilters:${filterToken}`
        : 'pending_issueDetailsFilters';
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try { rawDashboardFilters = JSON.parse(stored); } catch { }
        localStorage.removeItem(storageKey);
      } else if (filterToken) {
        const legacyStored = localStorage.getItem('pending_issueDetailsFilters');
        if (legacyStored) {
          try { rawDashboardFilters = JSON.parse(legacyStored); } catch { }
          localStorage.removeItem('pending_issueDetailsFilters');
        }
      }
    }
    const initialFilters = this.getInitialIssueFilters(rawDashboardFilters);
    this.hasDashboardFilter = !!rawDashboardFilters && Object.values(rawDashboardFilters).some(v => v !== null && v !== undefined && v !== '');
    history.replaceState({}, '', window.location.href);
   this.reviewerService.getIssueDetails(initialFilters)
   //this.reviewerService.getIssueEAV(null, null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          this.data = (res || []).map(item => ({
            reviewId: item.ReviewID ?? '',
            reviewName: item.ReviewName ?? '',
            currentStatus: item.CurrentStatus ?? item.Status ?? '',
            proposedYear: item.Review_ProposedYear ?? item.ProposedYear ?? '',
            masterClientName: item.Review_ClientName ?? item.Review_MasterClientName ?? item.ClientName ?? '',
            dntProjectName: item.Review_ProjectName ?? item['Review_D&TProjectName'] ?? item.Review_DTProjectName ?? item.ProjectName ?? '',
            contractNumber: item.Review_ContractNumber ?? item.ContractNumber ?? '',
            additionalContractNumber: item.Review_AdditionalContractNumber ?? item.AdditionalContractNumber ?? '',
            marketUnit: item.Review_MarketUnit ?? item.MarketUnit ?? '',
            customerRelationshipName: item.Review_CustomerRelationshipName ?? item.CustomerRelationshipName ?? '',
            planName: item.Review_PlanName ?? item.PlanName ?? '',
            qadRecommendation: item.Review_QADRecommendation ?? item.QADRecommendation ?? '',
            clientQualityCQI: item.Review_ClientQualityCQI ?? item.ClientQualityCQI ?? '',
            clientAccountLead: item.Review_ClientAccountLead ?? item.ClientAccountLead ?? '',
            masterClientID: item.Review_MasterClientID ?? item.MasterClientID ?? '',
            deliveryCountry: item.Review_DeliveryCountry ?? item.DeliveryCountry ?? '',
            locationsToBeReviewed: item.Review_LocationToBeReviewed ?? item.IssueLocationsList ?? item.Review_LocationName ?? '',
            isPrimaryLocation: (item.Review_IsParentLocation === '1' || item.IsParentLocation === true) ? 'Yes' : 'No',
            quarter: item.Review_Quarter ?? item.Quarter ?? '',
            proposedMonth: item.Review_ProposedMonth ?? item.ProposedMonth ?? '',
            rcManager: item.Review_RCManager ?? item.RCManager ?? '',
            rcSupervisor: item.Review_RCSupervisor ?? item.RCSupervisor ?? '',
            rcReviewer: item.Review_RCReviewer ?? item.RCReviewer ?? '',
            secondaryManager: item.Review_SecondaryRCManager ?? item.Review_SecondaryRcManager ?? item.Review_SecondaryManager ?? item.SecondaryManager ?? '',
            secondarySupervisor: item.Review_SecondaryRCSupervisor ?? item.Review_SecondaryRcSupervisor ?? item.Review_SecondarySupervisor ?? item.SecondarySupervisor ?? '',
            secondaryReviewer: item.Review_SecondaryRCReviewer ?? item.Review_SecondaryReviewer ?? item.SecondaryReviewer ?? '',
            accentureOperationsSL: item.Schedule_AccentureOperationsServiceLine ?? item.Schedule_ServiceLine ?? '',
            targetForReportPublications: item.Schedule_TargetPublicationDate ?? item.Schedule_TargetForReportPublication ?? item.TargetPublicationDate ?? '',
            actualPublicationDate: item.ActualPublicationDate ?? '',
            overallReportingRating: item.OverallReportingRating ?? '',
            issueId: item.IssueID ?? item.IssueId ?? '',
            issueTitle: item.IssueTitle ?? '',
            issueDescription: item.IssueDescription ?? '',
            imRcReviewer: item.RCReviewer || item.Review_RCReviewer || '',
            imRcSupervisor: item.RCSupervisor || item.Review_RCSupervisor || '',
            imRcManager: item.RCManager || item.Review_RCManager || '',
            identificationDate: item.IdentificationDate ?? '',
            subscribersNotify: (() => {
              const raw = item.IssueSubscribersList ?? item.SubscriberNames ?? item.Subscribers;
              if (Array.isArray(raw)) {
                return raw.map((s: any) => s.IssueSubscriber ?? s.EnterpriseId ?? s.Email ?? '').filter(Boolean).join(',');
              }
              return raw ?? '';
            })(),
            rcComments: item.RCComments ?? '',
            actionRequiredBy: item.ActionRequiredBy ?? '',
            issueRating: item.IssueRating ?? '',
            issueStatus: item.IssueStatus ?? '',
            locationReviewedImpacted: item.IssueLocationsList ?? item.LocationReviewedImpacted ?? '',
            riskStatementL3: item.RiskStatementL3 ?? '',
            linkedActionPlan: item.LinkedActionPlanNames ?? item.LinkedActions ?? '',
            reviewObjective: item.ReviewObjective ?? '',
            recommendation: item.Recommendation ?? '',
            broadIssueCategory: item.BroadIssueCategoryL1 ?? item.BroadIssueCategory ?? '',
            detailedIssueCategory: item.DetailedIssueCategoryL2 ?? item.DetailedIssueCategory ?? '',
            repLetterConflict: (() => {
              const rid = String(item.IssueID ?? item.IssueId ?? '').replace(/^[A-Za-z]+-/i, '').trim();
              try { const c = localStorage.getItem(`ast_rlc_${rid}`); if (c !== null) return c; } catch {}
              const v = item.RepLetterConflict;
              if (v === true  || String(v).toLowerCase() === 'true' || v==='1'  || String(v).toLowerCase() === 'yes')           return 'Yes';
              if (v === false || String(v).toLowerCase() === 'false' || v==='0' || String(v).toLowerCase() === 'no')            return 'No';
              if (String(v).toLowerCase() === 'not applicable')                                                                   return 'Not Applicable';
              return v ?? '';
            })(),
            repLetterQuestionsWithConflict: item.RepLetterQuestions ?? item.RepLetterQuestionText ?? item['RepLetter Questions with Conflict'] ?? '',
            serviceLine: item.ServiceLine ?? item['Accenture Operation Service Line'] ?? '',
            reviewTypeId: item.Review_ReviewTypeID ?? null,
            typeOfReview: item.Review_TypeOfReview ?? item.TypeOfReview ?? '',
          }));
          this.applySort();
          // Enrich grid rows from localStorage — the list API may return incorrect linked AP data
          // due to a backend JOIN issue. The locally-cached value (set when the user saved the issue)
          // is the authoritative source, including an empty array meaning "no linked APs".
          this.data = this.data.map((row: any) => {
            const id = String(row.issueId || '').replace(/^[A-Za-z]+-/i, '').trim();
            if (!id) return row;
            try {
              // Highest priority: state written by issue-view-edit after UpdateActionPlanLink save.
              const iveState = localStorage.getItem(`ast_ive_state_${id}`);
              if (iveState !== null) {
                const aps = JSON.parse(iveState);
                if (Array.isArray(aps)) {
                  const linkedApIds = aps
                    .filter((ap: any) => ap.linked)
                    .map((ap: any) => String(ap.id ?? '').replace(/^AP-/i, '').trim())
                    .filter(Boolean);
                  try { localStorage.setItem(`ast_linked_aps_${id}`, JSON.stringify(linkedApIds)); } catch {}
                  try { localStorage.removeItem(`ast_ive_state_${id}`); } catch {}
                  return { ...row, linkedActionPlan: linkedApIds.map((apId: string) => 'AP-' + apId).join(',') };
                }
              }
              const lsLinked = localStorage.getItem(`ast_linked_aps_${id}`);
              if (lsLinked !== null) {
                const arr = JSON.parse(lsLinked);
                if (Array.isArray(arr) && arr.length > 0) {
                  return { ...row, linkedActionPlan: arr.map((apId: string) => 'AP-' + apId).join(',') };
                }
              }
            } catch {}
            return row;
          });
          this.defaultOrder = [...this.data];
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          console.error('getIssueEAV error:', err);
        }
      });
  }

  clearDashboardFilter(): void {
    this.hasDashboardFilter = false;
    this.isLoading = true;
    this.reviewerService.getIssueDetails(this.getInitialIssueFilters())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any[]) => {
          this.data = (res || []).map(item => ({
            reviewId: item.ReviewID ?? '',
            reviewName: item.ReviewName ?? '',
            currentStatus: item.CurrentStatus ?? item.Status ?? '',
            proposedYear: item.Review_ProposedYear ?? item.ProposedYear ?? '',
            masterClientName: item.Review_ClientName ?? item.Review_MasterClientName ?? item.ClientName ?? '',
            dntProjectName: item.Review_ProjectName ?? item['Review_D&TProjectName'] ?? item.Review_DTProjectName ?? item.ProjectName ?? '',
            contractNumber: item.Review_ContractNumber ?? item.ContractNumber ?? '',
            additionalContractNumber: item.Review_AdditionalContractNumber ?? item.AdditionalContractNumber ?? '',
            marketUnit: item.Review_MarketUnit ?? item.MarketUnit ?? '',
            customerRelationshipName: item.Review_CustomerRelationshipName ?? item.CustomerRelationshipName ?? '',
            planName: item.Review_PlanName ?? item.PlanName ?? '',
            qadRecommendation: item.Review_QADRecommendation ?? item.QADRecommendation ?? '',
            clientQualityCQI: item.Review_ClientQualityCQI ?? item.ClientQualityCQI ?? '',
            clientAccountLead: item.Review_ClientAccountLead ?? item.ClientAccountLead ?? '',
            masterClientID: item.Review_MasterClientID ?? item.MasterClientID ?? '',
            deliveryCountry: item.Review_DeliveryCountry ?? item.DeliveryCountry ?? '',
            locationsToBeReviewed: item.Review_LocationToBeReviewed ?? item.IssueLocationsList ?? item.Review_LocationName ?? '',
            isPrimaryLocation: (item.Review_IsParentLocation === '1' || item.IsParentLocation === true) ? 'Yes' : 'No',
            quarter: item.Review_Quarter ?? item.Quarter ?? '',
            proposedMonth: item.Review_ProposedMonth ?? item.ProposedMonth ?? '',
            rcManager: item.Review_RCManager ?? item.RCManager ?? '',
            rcSupervisor: item.Review_RCSupervisor ?? item.RCSupervisor ?? '',
            rcReviewer: item.Review_RCReviewer ?? item.RCReviewer ?? '',
            secondaryManager: item.Review_SecondaryRCManager ?? item.Review_SecondaryRcManager ?? item.Review_SecondaryManager ?? item.SecondaryManager ?? '',
            secondarySupervisor: item.Review_SecondaryRCSupervisor ?? item.Review_SecondaryRcSupervisor ?? item.Review_SecondarySupervisor ?? item.SecondarySupervisor ?? '',
            secondaryReviewer: item.Review_SecondaryRCReviewer ?? item.Review_SecondaryReviewer ?? item.SecondaryReviewer ?? '',
            accentureOperationsSL: item.Schedule_AccentureOperationsServiceLine ?? item.Schedule_ServiceLine ?? '',
            targetForReportPublications: item.Schedule_TargetPublicationDate ?? item.Schedule_TargetForReportPublication ?? item.TargetPublicationDate ?? '',
            actualPublicationDate: item.ActualPublicationDate ?? '',
            overallReportingRating: item.OverallReportingRating ?? '',
            issueId: item.IssueID ?? item.IssueId ?? '',
            issueTitle: item.IssueTitle ?? '',
            issueDescription: item.IssueDescription ?? '',
            imRcReviewer: item.RCReviewer || item.Review_RCReviewer || '',
            imRcSupervisor: item.RCSupervisor || item.Review_RCSupervisor || '',
            imRcManager: item.RCManager || item.Review_RCManager || '',
            identificationDate: item.IdentificationDate ?? '',
            subscribersNotify: (() => {
              const raw = item.IssueSubscribersList ?? item.SubscriberNames ?? item.Subscribers;
              if (Array.isArray(raw)) {
                return raw.map((s: any) => s.IssueSubscriber ?? s.EnterpriseId ?? s.Email ?? '').filter(Boolean).join(',');
              }
              return raw ?? '';
            })(),
            rcComments: item.RCComments ?? '',
            actionRequiredBy: item.ActionRequiredBy ?? '',
            issueRating: item.IssueRating ?? '',
            issueStatus: item.IssueStatus ?? '',
            locationReviewedImpacted: item.IssueLocationsList ?? item.LocationReviewedImpacted ?? '',
            riskStatementL3: item.RiskStatementL3 ?? '',
            linkedActionPlan: item.LinkedActionPlanNames ?? item.LinkedActions ?? '',
            reviewObjective: item.ReviewObjective ?? '',
            recommendation: item.Recommendation ?? '',
            broadIssueCategory: item.BroadIssueCategoryL1 ?? item.BroadIssueCategory ?? '',
            detailedIssueCategory: item.DetailedIssueCategoryL2 ?? item.DetailedIssueCategory ?? '',
            repLetterConflict: (() => {
              const rid = String(item.IssueID ?? item.IssueId ?? '').replace(/^[A-Za-z]+-/i, '').trim();
              try { const c = localStorage.getItem(`ast_rlc_${rid}`); if (c !== null) return c; } catch {}
              const v = item.RepLetterConflict;
              if (v === true  || String(v).toLowerCase() === 'true' || v==='1'  || String(v).toLowerCase() === 'yes') return 'Yes';
              if (v === false || String(v).toLowerCase() === 'false' || v==='0' || String(v).toLowerCase() === 'no')  return 'No';
              if (String(v).toLowerCase() === 'not applicable') return 'Not Applicable';
              return v ?? '';
            })(),
            repLetterQuestionsWithConflict: item.RepLetterQuestions ?? item.RepLetterQuestionText ?? item['RepLetter Questions with Conflict'] ?? '',
            serviceLine: item.ServiceLine ?? item['Accenture Operation Service Line'] ?? '',
            reviewTypeId: item.Review_ReviewTypeID ?? null,
            typeOfReview: item.Review_TypeOfReview ?? item.TypeOfReview ?? '',
          }));
          this.applySort();
          this.data = this.data.map((row: any) => {
            const id = String(row.issueId || '').replace(/^[A-Za-z]+-/i, '').trim();
            if (!id) return row;
            try {
              const iveState = localStorage.getItem(`ast_ive_state_${id}`);
              if (iveState !== null) {
                const aps = JSON.parse(iveState);
                if (Array.isArray(aps)) {
                  const linkedApIds = aps.filter((ap: any) => ap.linked).map((ap: any) => String(ap.id ?? '').replace(/^AP-/i, '').trim()).filter(Boolean);
                  try { localStorage.setItem(`ast_linked_aps_${id}`, JSON.stringify(linkedApIds)); } catch {}
                  try { localStorage.removeItem(`ast_ive_state_${id}`); } catch {}
                  return { ...row, linkedActionPlan: linkedApIds.map((apId: string) => 'AP-' + apId).join(',') };
                }
              }
              const lsLinked = localStorage.getItem(`ast_linked_aps_${id}`);
              if (lsLinked !== null) {
                const arr = JSON.parse(lsLinked);
                if (Array.isArray(arr) && arr.length > 0) {
                  return { ...row, linkedActionPlan: arr.map((apId: string) => 'AP-' + apId).join(',') };
                }
              }
            } catch {}
            return row;
          });
          this.defaultOrder = [...this.data];
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          console.error('clearDashboardFilter error:', err);
        },
      });
  }

  private getInitialIssueFilters(filters?: Partial<IssueDetailsFilters>): IssueDetailsFilters {
      return {
        ReviewID: filters?.ReviewID ?? null,
        PropsedYearId: filters?.PropsedYearId ?? null,
        Quarter: filters?.Quarter ?? null,
        ProposedMonth: filters?.ProposedMonth ?? null,
        ClientName: filters?.ClientName ?? null,
        DeliveryCountry: filters?.DeliveryCountry ?? null,
        LocationToBeReviewed: filters?.LocationToBeReviewed ?? null,
        TeamMembers: filters?.TeamMembers ?? null,
        Region: filters?.Region ?? null,
        IssueID: filters?.IssueID ?? null,
        ReviewTypeID: filters?.ReviewTypeID ?? null,
        IssueStatus: filters?.IssueStatus ?? null,
        IssueRatings: filters?.IssueRatings ?? null,
        IssueCategory: filters?.IssueCategory ?? null,
      };
  }

  private readonly dateSortCols = new Set([
    'identificationDate', 'targetForReportPublications', 'actualPublicationDate',
  ]);

  private readonly monthOrder: Record<string, number> = {
    Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
    Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
  };

  private parseDateSortKey(val: string): number {
    const dmy = /^(\d{2})-([A-Za-z]{3})-(\d{4})$/.exec(val);
    if (dmy) return +dmy[3] * 10000 + (this.monthOrder[dmy[2]] ?? 0) * 100 + +dmy[1];
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(val);
    if (iso) return +iso[1] * 10000 + +iso[2] * 100 + +iso[3];
    return -1;
  }

  private compareForSort(va: string, vb: string, col: string): number {
    if (this.dateSortCols.has(col)) return this.parseDateSortKey(va) - this.parseDateSortKey(vb);
    if (va === '' && vb === '') return 0;
    if (va === '') return -1;
    if (vb === '') return 1;
    const aDigit = /^\d/.test(va);
    const bDigit = /^\d/.test(vb);
    if (aDigit !== bDigit) return aDigit ? -1 : 1;
    return va.localeCompare(vb, 'en', { numeric: true, sensitivity: 'base' });
  }

  private applySort() {
    const col = this.sortColumn || 'identificationDate';
    const dir = this.sortDirection;
    this.data.sort((a, b) => {
      const va = String(a[col] ?? '').trim();
      const vb = String(b[col] ?? '').trim();
      const cmp = this.compareForSort(va, vb, col);
      return dir === 'asc' ? cmp : -cmp;
    });
  }

  getIssueRatingClass(rating: string): string {
    switch ((rating || '').toLowerCase()) {
      case 'red':    return 'badge-red';
      case 'yellow': return 'badge-yellow';
      case 'blue':   return 'badge-blue';
      case 'green':  return 'badge-green';
      default:       return 'badge-not-rated';
    }
  }

  isColumnVisible(key: string): boolean {
    const col = this.exportableColumns().find(c => c.key === key);
    return col ? col.visible : false;
  }

  visibleColumnCount(): number {
    return this.exportableColumns().filter(c => c.visible).length;
  }

  totalColumnCount(): number {
    return this.exportableColumns().length;
  }

  selectAll(): void {
    [...this.reviewBasicColumns, ...this.scheduleColumns, ...this.reviewCycleColumns, ...this.issueManagementColumns].forEach(c => c.visible = true);
    this.saveFilterState();
  }

  clearAll(): void {
    [...this.reviewBasicColumns, ...this.scheduleColumns, ...this.reviewCycleColumns, ...this.issueManagementColumns].forEach(c => {
      c.visible = this.defaultVisibleKeys.has(c.key);
    });
    this.saveFilterState();
  }

  saveFilterState(): void {
    const state = {
      columnVisibilityStateVersion: this.columnVisibilityStateVersion,
      filters: this.filters,
      searchText: this.searchText,
      columnVisibility: {
        reviewBasicColumns: this.reviewBasicColumns.map(c => ({ key: c.key, visible: c.visible })),
        scheduleColumns: this.scheduleColumns.map(c => ({ key: c.key, visible: c.visible })),
        reviewCycleColumns: this.reviewCycleColumns.map(c => ({ key: c.key, visible: c.visible })),
        issueManagementColumns: this.issueManagementColumns.map(c => ({ key: c.key, visible: c.visible })),
      },
    };
    sessionStorage.setItem('im_filter_state', JSON.stringify(state));
  }

  private restoreFilterState(): void {
    const raw = sessionStorage.getItem('im_filter_state');
    if (!raw) return;
    try {
      const state = JSON.parse(raw);
      if (state.filters) {
        this.filterableColumns.forEach(col => {
          if (state.filters[col]) this.filters[col] = state.filters[col];
        });
      }
      if (typeof state.searchText === 'string') this.searchText = state.searchText;
      if (
        state.columnVisibilityStateVersion === this.columnVisibilityStateVersion &&
        state.columnVisibility
      ) {
        this.applyColumnVisibility(this.reviewBasicColumns, state.columnVisibility.reviewBasicColumns);
        this.applyColumnVisibility(this.scheduleColumns, state.columnVisibility.scheduleColumns);
        this.applyColumnVisibility(this.reviewCycleColumns, state.columnVisibility.reviewCycleColumns);
        this.applyColumnVisibility(this.issueManagementColumns, state.columnVisibility.issueManagementColumns);
      }
    } catch {
      sessionStorage.removeItem('im_filter_state');
    }
  }

  private applyColumnVisibility(cols: ColumnItem[], saved: { key: string; visible: boolean }[] | undefined): void {
    if (!Array.isArray(saved)) return;
    const map = new Map(saved.map((s: { key: string; visible: boolean }) => [s.key, s.visible]));
    cols.forEach(c => { if (map.has(c.key)) c.visible = map.get(c.key)!; });
  }

  private exportData(): any[] {
    if (!this.defaultOrder.length) return this.data;
    const currentRows = new Set(this.data);
    const savedRows = new Set(this.defaultOrder);
    return [
      ...this.defaultOrder.filter(row => currentRows.has(row)),
      ...this.data.filter(row => !savedRows.has(row)),
    ];
  }

  /** Returns every data column that may be exported, excluding grid action controls. */
  private exportableColumns(): ColumnItem[] {
    return [
      ...this.issueManagementColumns,
      ...this.reviewBasicColumns,
      ...this.scheduleColumns,
      ...this.reviewCycleColumns,
    ];
  }

  /** Exports all issues across all reviews with all exportable system columns, independent of selector state. */
  async exportAll(): Promise<void> {
    if (this.isExporting) return;
    this.isExporting = true;
    try {
      const columns = this.exportableColumns().map(column => ({
        key: column.key,
        header: column.label,
      }));
      const fileName = this.excelExportSvc.buildFileName('IssueManagement_ExportAll');
      await this.excelExportSvc.exportToExcel(columns, this.exportData(), fileName, 'Issue Management Export All');
    } catch (err) {
      console.error('Export All failed:', err);
    } finally {
      this.isExporting = false;
    }
  }

  /** Exports every issue record with only the columns enabled in the column selector. */
  async exportVisible(): Promise<void> {
    if (this.isExporting) return;
    this.isExporting = true;
    try {
      const columns = this.exportableColumns()
        .filter(column => column.visible)
        .map(column => ({
          key: column.key,
          header: column.label,
        }));
      const fileName = this.excelExportSvc.buildFileName('IssueManagement_ExportVisible');
      await this.excelExportSvc.exportToExcel(columns, this.exportData(), fileName, 'Issue Management Export Visible');
    } catch (err) {
      console.error('Export Visible failed:', err);
    } finally {
      this.isExporting = false;
    }
  }

  filteredBasicColumns(): ColumnItem[] {
    return this.applySearch(this.reviewBasicColumns);
  }

  filteredScheduleColumns(): ColumnItem[] {
    return this.applySearch(this.scheduleColumns);
  }

  filteredReviewCycleColumns(): ColumnItem[] {
    return this.applySearch(this.reviewCycleColumns);
  }

  filteredIssueManagementColumns(): ColumnItem[] {
    return this.applySearch(this.issueManagementColumns);
  }

  private applySearch(cols: ColumnItem[]): ColumnItem[] {
    const q = (this.columnSearchText || '').toLowerCase().trim();
    return q ? cols.filter(c => c.label.toLowerCase().includes(q)) : cols;
  }

  isBasicExpanded(): boolean {
    const hasSearch = !!this.columnSearchText.trim();
    if (hasSearch) return this.filteredBasicColumns().length > 0;
    return this.basicExpanded;
  }

  isScheduleExpanded(): boolean {
    const hasSearch = !!this.columnSearchText.trim();
    if (hasSearch) return this.filteredScheduleColumns().length > 0;
    return this.scheduleExpanded;
  }

  isReviewCycleExpanded(): boolean {
    const hasSearch = !!this.columnSearchText.trim();
    if (hasSearch) return this.filteredReviewCycleColumns().length > 0;
    return this.reviewCycleExpanded;
  }

  isIssueManagementExpanded(): boolean {
    const hasSearch = !!this.columnSearchText.trim();
    if (hasSearch) return this.filteredIssueManagementColumns().length > 0;
    return this.issueManagementExpanded;
  }

  toggleFilterPanel() {
    this.isFilterOpen = !this.isFilterOpen;
  }

  toggleColumnFilter(col: string, event: Event) {
    event.stopPropagation();
    this.activeFilterColumn = this.activeFilterColumn === col ? null : col;
  }

  applyFilter() {
    this.activeFilterColumn = null;
    this.currentPage = 1;
    this.saveFilterState();
  }

  resetFilter(col: string) {
    this.filters[col] = { op1: 'Contains', val1: '' };
    this.currentPage = 1;
    this.saveFilterState();
  }

  hasActiveFilter(col: string): boolean {
    const f = this.filters[col];
    return !!(f && f.val1);
  }

  private matchOperator(cell: string, op: string, val: string): boolean {
    if (!val) return true;
    const c = cell.toLowerCase();
    const v = val.toLowerCase();
    switch (op) {
      case 'Contains':     return c.includes(v);
      case 'Not Contains': return !c.includes(v);
      case 'Equals':       return c === v;
      case 'Starts With':  return c.startsWith(v);
      case 'Ends With':    return c.endsWith(v);
      default:             return true;
    }
  }

  filteredData(): any[] {
    const term = this.searchText.trim().toLowerCase();
    return this.data.filter(row => {
      if (term) {
        const visibleKeys = [
          ...this.reviewBasicColumns,
          ...this.scheduleColumns,
          ...this.reviewCycleColumns,
          ...this.issueManagementColumns,
        ].filter(c => c.visible).map(c => c.key);
        const matches = visibleKeys.some(col =>
          String((row as any)[col] ?? '').toLowerCase().includes(term)
        );
        if (!matches) return false;
      }
      return this.filterableColumns.every(col => {
        const f = this.filters[col];
        if (!f || !f.val1) return true;
        return this.matchOperator(String((row as any)[col] ?? ''), f.op1, f.val1);
      });
    });
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredData().length / this.pageSize));
  }

  totalPagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 10;
    const startPage = Math.floor((this.currentPage - 1) / maxVisible) * maxVisible + 1;
    const endPage = Math.min(startPage + maxVisible - 1, total);
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  goToPage() {
    if (this.goToPageInput >= 1 && this.goToPageInput <= this.totalPages()) {
      this.currentPage = this.goToPageInput;
    }
  }

  firstPage() { this.currentPage = 1; this.goToPageInput = 1; }

  prevPage() { if (this.currentPage > 1) this.currentPage--; }

  nextPage() { if (this.currentPage < this.totalPages()) this.currentPage++; }

  lastPage() { this.currentPage = this.totalPages(); this.goToPageInput = this.currentPage; }

  sort(column: string) {
    this.activeFilterColumn = null;
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applySort();
    this.currentPage = 1;
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'unfold_more';
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  viewIssue(item: any): void {
    this.openIssue(item, 'view', true);
  }

  editIssue(item: any): void {
    const status = (item.issueStatus ?? '').toLowerCase().replace(/[^a-z]/g, '');
    if ((this.isSupervisor || this.isSupervisorOrManagerForRow(item)) && status === 'rejected') {
      this.openIssue(item, 'edit');
    } else if (status === 'draft' || status === 'rejected') {
      this.openEditIssueModal(item);
    } else if (this.isAdmin || this.isAssignedToRow(item)) {
      this.openIssue(item, 'edit');
    } else {
      this.openIssue(item, 'view', false);
    }
  }

  private openIssue(item: any, mode: 'view' | 'edit', fromViewIcon = false): void {
    const raw = String(item.issueId ?? '');
    const param = /^i-/i.test(raw) ? raw : `I-${raw}`;
    const isGoLive = Number(item.reviewTypeId) === 2;
    const urlTree = this.router.createUrlTree(['/issue-management', param], {
      queryParams: { mode, reviewId: Number(item.reviewId) || null, isGoLive: isGoLive ? '1' : '0', linkedActionPlan: item.linkedActionPlan ?? '', fromViewIcon: fromViewIcon ? '1' : '0' }
    });
    window.open(this.router.serializeUrl(urlTree), '_blank');
  }
 

  closeAddIssueModal(): void {
    this.addIssueModalOpen = false;
    this.editingIssueData = null;
    this.editingIssueDocuments = [];
    this.editingReviewId = null;
    this.editingReviewTypeId = 1;
    this.editingTypeOfReview = '';
  }

  openEditIssueModal(item: any): void {
    const issueId = Number(String(item.issueId).replace(/^[A-Za-z]+-/i, '').trim());
    const reviewId = Number(item.reviewId) || null;
    this.editingReviewId = reviewId;
    this.editingReviewTypeId = Number(item.reviewTypeId) || 1;
    this.editingTypeOfReview = item.typeOfReview ?? '';

    let cachedSubscribers = '';
    const normalizedId = String(item.issueId).replace(/^[A-Za-z]+-/i, '').trim();
    const memorySubs = this.issueSubscriberCache.get(normalizedId);
    if (memorySubs?.length) {
      cachedSubscribers = memorySubs.join(',');
    } else {
      try {
        const cached = localStorage.getItem(`ast_subs_${normalizedId}`);
        if (cached) {
          const arr = JSON.parse(cached);
          cachedSubscribers = Array.isArray(arr) ? arr.join(',') : String(arr);
        }
      } catch {}
    }

    const cachedRlc = (() => { try { return localStorage.getItem(`ast_rlc_${normalizedId}`); } catch { return null; } })();
    let cachedLinkedAps: string | null = null;
    try {
      const lsLinked = localStorage.getItem(`ast_linked_aps_${normalizedId}`);
      if (lsLinked !== null) {
        const arr = JSON.parse(lsLinked);
        cachedLinkedAps = Array.isArray(arr) ? arr.map((apId: string) => 'AP-' + apId).join(',') : String(arr);
      }
    } catch {}
    this.editingIssueData = this.toApiIssueShape(item, cachedSubscribers);
    if (cachedRlc !== null) this.editingIssueData = { ...this.editingIssueData, RepLetterConflict: cachedRlc };
    if (cachedLinkedAps !== null) this.editingIssueData = { ...this.editingIssueData, LinkedActionPlan: cachedLinkedAps };
    this.editingIssueDocuments = [];
    this.isLoading = true;

    this.reviewerService.getIssueEAV(reviewId, issueId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const raw = Array.isArray(res)
            ? res[0]
            : (res?.issues?.[0] ?? res?.Issues?.[0] ?? res?.Issue?.[0] ?? res);
          if (raw && typeof raw === 'object') {
            try {
              const docsField = raw.IssueDocumentsList ?? raw.Documents ?? raw.DocumentsJSON;
              const docs = docsField
                ? (typeof docsField === 'string' ? JSON.parse(docsField) : docsField)
                : [];
              this.editingIssueDocuments = Array.isArray(docs) ? docs : [];
            } catch { this.editingIssueDocuments = []; }

            if (!this.editingIssueDocuments.length) {
              try {
                const ls = localStorage.getItem(`ast_docs_${issueId}`);
                if (ls) { const arr = JSON.parse(ls); this.editingIssueDocuments = Array.isArray(arr) ? arr : []; }
              } catch {}
            }

            const rawSubs = raw.IssueSubscribersList || raw.Subscribers || raw.SubscriberNames || raw.NotifyEmails || raw.Notify || '';
            const apiSubs = Array.isArray(rawSubs)
              ? rawSubs.map((s: any) => s.IssueSubscriber ?? s.EnterpriseId ?? s.Email ?? '').filter(Boolean).join(',')
              : rawSubs;
            const finalSubs = apiSubs || cachedSubscribers || item.subscribersNotify || '';
            this.editingIssueData = {
              ...raw,
              ...(cachedRlc !== null ? { RepLetterConflict: cachedRlc } : {}),
              LinkedActionPlan: cachedLinkedAps !== null ? cachedLinkedAps : (item.linkedActionPlan ?? ''),
              IssueRCReviewerID:  raw.RCReviewerID  ?? '',
              IssueRCSupervisorID: raw.RCSupervisorID ?? '',
              IssueRCManagerID:   raw.RCManagerID   ?? '',
              IssueRCReviewer:    raw.RCReviewer    ?? '',
              IssueRCSupervisor:  raw.RCSupervisor  ?? '',
              IssueRCManager:     raw.RCManager     ?? '',
              IssueRepLetterQuarter: raw.Quarter ?? raw.Review_Quarter ?? '',
              IssueFinancialYear:    raw.FinancialYear ?? raw.FinancialYearName ?? '',
              IssueMaxTargetDate:    raw.IssueCloseDueDate ?? raw.MaxTargetDate ?? '',
              IssueReportStatus:     raw.ReportStatus ?? '',
              LocationNames: raw.IssueLocationsList ?? raw.Locations ?? raw.LocationNames ?? '',
              Subscribers: finalSubs,
              ServiceLine: raw['Accenture Operation Service Line'] ?? raw.ServiceLine ?? '',
              RepLetterQuestionsWithConflict: raw['RepLetter Questions with Conflict'] ?? raw.RepLetterQuestions ?? raw.RepLetterQuestionsWithConflict ?? '',
            };
          }
          this.isLoading = false;
          this.loadModalData(reviewId);
        },
        error: () => {
          try {
            const ls = localStorage.getItem(`ast_docs_${issueId}`);
            if (ls) { const arr = JSON.parse(ls); this.editingIssueDocuments = Array.isArray(arr) ? arr : []; }
          } catch {}
          this.isLoading = false;
          this.loadModalData(reviewId);
        },
      });
  }

  private toApiIssueShape(item: any, subscribers: string): any {
    return {
      IssueID:              item.issueId,
      IssueStatus:          item.issueStatus,
      IssueTitle:           item.issueTitle,
      IssueDescription:     item.issueDescription,
      IssueRCReviewer:      item.imRcReviewer,
      IssueRCSupervisor:    item.imRcSupervisor,
      IssueRCManager:       item.imRcManager,
      IdentificationDate:   item.identificationDate,
      RCComments:           item.rcComments,
      ActionRequiredBy:     item.actionRequiredBy,
      IssueRating:          item.issueRating,
      ReviewObjective:      item.reviewObjective,
      Recommendation:       item.recommendation,
      BroadIssueCategoryL1:       item.broadIssueCategory,
      DetailedIssueCategoryL2:    item.detailedIssueCategory,
      RiskStatementL3:            item.riskStatementL3,
      RepLetterConflict:          item.repLetterConflict,
      RepLetterQuestionsWithConflict: item.repLetterQuestionsWithConflict,
      ServiceLine:          item.serviceLine,
      LocationNames:        item.locationReviewedImpacted,
      LinkedActionPlan:     item.linkedActionPlan,
      Subscribers:          item.subscribersNotify || subscribers,
    };
  }

  private loadModalData(reviewId: number | null): void {
    this.isLoading = true;
    this.loadRepLetterQuestions();
    this.loadRiskCategories();
    this.loadModalLocations(reviewId);
    this.loadNextIssueId(reviewId);
    this.loadNextActionPlanId(reviewId);
    this.loadActionPlans(reviewId);
    this.loadInternalDependencies();
    this.loadReviewRcPersonnel(reviewId);
    this.reviewerService.getServiceLines().subscribe({
      next: (response: any) => {
        if (Array.isArray(response)) {
          const lines: string[] = response
            .map((i: any) => i.ServiceLineName ?? i.Name ?? i)
            .filter((v: any) => typeof v === 'string' && v.trim().length > 0);
          if (lines.length) this.serviceLines = lines;
        }
        this.isLoading = false;
        this.addIssueModalOpen = true;
      },
      error: () => { this.isLoading = false; this.addIssueModalOpen = true; },
    });
  }

  async onSaveIssueDraft(payload: { step: 1 | 2; form: any; files: any[]; actionPlans: any[]; newActionPlans: any[]; newActionPlanFiles: any[][]; deletedFilePaths?: string[] }): Promise<void> {
    const rawId = String(payload.form.issueId || '').replace(/^[A-Za-z]+-/i, '').trim();
    if (rawId && payload.form.subscribers?.length) {
      try { localStorage.setItem(`ast_subs_${rawId}`, JSON.stringify(payload.form.subscribers)); } catch {}
    }
    if (rawId) { try { localStorage.setItem(`ast_rlc_${rawId}`, payload.form.repLetterConflict ?? ''); } catch {} }
    if (rawId) {
      try {
        const apIds = (payload.actionPlans ?? []).map((ap: any) => String(ap.id ?? '').replace(/^AP-/i, '').trim()).filter(Boolean);
        localStorage.setItem(`ast_linked_aps_${rawId}`, JSON.stringify(apIds));
      } catch {}
      try {
        const prev: any[] = (() => { try { return JSON.parse(localStorage.getItem(`ast_docs_${rawId}`) ?? '[]'); } catch { return []; } })();
        const deleted = new Set<string>(payload.deletedFilePaths ?? []);
        const kept = prev.filter((f: any) => !deleted.has(f.StorageFilePath));
        const added = (payload.files as File[]).map((f: File) => ({
          FileName: f.name,
          StorageFilePath: `${rawId}/${payload.form.issueId}/${f.name}`,
          FileSize: f.size,
          FileType: f.name.split('.').pop() ?? '',
        }));
        localStorage.setItem(`ast_docs_${rawId}`, JSON.stringify([...kept, ...added]));
      } catch {}
    }
    await this.submitIssue(payload, 'Draft', payload.form.reportStatus ?? '');
  }

  async onSendForApproval(payload: { step: 1 | 2; form: any; files: any[]; actionPlans: any[]; newActionPlans: any[]; newActionPlanFiles: any[][]; deletedFilePaths?: string[] }): Promise<void> {
    const rawId = String(payload.form.issueId || '').replace(/^[A-Za-z]+-/i, '').trim();
    if (rawId && payload.form.subscribers?.length) {
      try { localStorage.setItem(`ast_subs_${rawId}`, JSON.stringify(payload.form.subscribers)); } catch {}
    }
    if (rawId) { try { localStorage.setItem(`ast_rlc_${rawId}`, payload.form.repLetterConflict ?? ''); } catch {} }
    if (rawId) {
      try {
        const apIds = (payload.actionPlans ?? []).map((ap: any) => String(ap.id ?? '').replace(/^AP-/i, '').trim()).filter(Boolean);
        localStorage.setItem(`ast_linked_aps_${rawId}`, JSON.stringify(apIds));
      } catch {}
      try {
        const prev: any[] = (() => { try { return JSON.parse(localStorage.getItem(`ast_docs_${rawId}`) ?? '[]'); } catch { return []; } })();
        const deleted = new Set<string>(payload.deletedFilePaths ?? []);
        const kept = prev.filter((f: any) => !deleted.has(f.StorageFilePath));
        const added = (payload.files as File[]).map((f: File) => ({
          FileName: f.name,
          StorageFilePath: `${rawId}/${payload.form.issueId}/${f.name}`,
          FileSize: f.size,
          FileType: f.name.split('.').pop() ?? '',
        }));
        localStorage.setItem(`ast_docs_${rawId}`, JSON.stringify([...kept, ...added]));
      } catch {}
    }
    await this.submitIssue(payload, 'Inactive', payload.form.reportStatus ?? '');
  }

  private async submitIssue(
    payload: { step: 1 | 2; form: any; files: any[]; actionPlans: any[]; newActionPlans: any[]; newActionPlanFiles: any[][] },
    actionType: string,
    reportStatus: string,
  ): Promise<void> {
    const form = payload.form;
    // Capture reviewId synchronously before any await — closeAddIssueModal() zeros it out
    // when the modal's close() emits, which happens between the saveDraft emit and the first
    // microtask tick that resumes this async function.
    const reviewId = this.editingReviewId;
    // Strip the display prefix ("I-357" → "357") so the API receives a numeric ID and
    // performs an UPDATE on the existing record instead of creating a new one.
    const issueIdForUpdate = (() => {
      if (!this.editingIssueData) return null;
      const id = String(form.issueId || '').replace(/^[A-Za-z]+-/i, '').trim();
      return id || null;
    })();
    this.addIssueModalOpen = false;
    this.isLoading = true;

    const documents = await Promise.all(
      (payload.files ?? []).map(async (file: File) => {
        const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '') : '';
        const fileContent = await this.readFileAsBase64(file);
        return {
          FileName: file.name,
          StorageFilePath: `${reviewId}/${form.issueId}/${file.name}`,
          fileContent, DocumentType: '', FileSize: file.size, FileType: ext, Description: '',
          UploadedBy: this.rebarAuth.getUserName() ?? '',
        };
      }),
    );

    const repLetterYes = form.repLetterConflict === 'Yes';
    const issueFields = [
      { FieldName: 'IssueTitle',              FieldValue: form.issueTitle ?? '',                                                                                                   DataType: 'NVARCHAR' },
      { FieldName: 'IssueDescription',        FieldValue: form.issueDescription ?? '',                                                                                            DataType: 'NVARCHAR' },
      { FieldName: 'ActionRequiredBy',        FieldValue: form.actionRequiredBy ?? '',                                                                                            DataType: 'NVARCHAR' },
      { FieldName: 'BroadIssueCategoryL1',    FieldValue: form.broadCategory ?? '',                                                                                               DataType: 'NVARCHAR' },
      { FieldName: 'DetailedIssueCategoryL2', FieldValue: form.detailedCategory ?? '',                                                                                            DataType: 'NVARCHAR' },
      { FieldName: 'RiskStatementL3',         FieldValue: form.riskStatement ?? '',                                                                                               DataType: 'NVARCHAR' },
      { FieldName: 'FinancialYear',           FieldValue: this.toFinancialYearId(form.financialYear) ?? '',                                                                        DataType: 'NVARCHAR' },
      { FieldName: 'Quarter',                 FieldValue: form.quarter ?? '',                                                                                                      DataType: 'NVARCHAR' },
      { FieldName: 'IdentificationDate',      FieldValue: this.toDisplayDate(form.identificationDate),                                                                            DataType: 'DATE'     },
      ...(form.issueCloseDueDate               ? [{ FieldName: 'IssueCloseDueDate',       FieldValue: form.issueCloseDueDate,                                                    DataType: 'DATE' }] : []),
      { FieldName: 'IssueRating',             FieldValue: form.issueRating ?? '',                                                                                                 DataType: 'NVARCHAR' },
      { FieldName: 'ReportStatus',            FieldValue: reportStatus,                                                                                                           DataType: 'NVARCHAR' },
      { FieldName: 'RepLetterConflict',       FieldValue: form.repLetterConflict === 'Yes' ? 'Yes' : form.repLetterConflict === 'No' ? 'No' : 'Not Applicable',                    DataType: 'NVARCHAR' },
      { FieldName: 'RepLetterQuestions',      FieldValue: repLetterYes && form.repLetterQuestionsWithConflict?.length ? form.repLetterQuestionsWithConflict.join(',') : 'N/A',    DataType: 'NVARCHAR' },
      { FieldName: 'Recommendation',          FieldValue: form.recommendation ?? '',                                                                                              DataType: 'NVARCHAR' },
      { FieldName: 'ReviewObjective',         FieldValue: form.reviewObjective ?? '',                                                                                             DataType: 'NVARCHAR' },
      { FieldName: 'ServiceLine',             FieldValue: (form.serviceLines ?? []).join(','),                                                                                    DataType: 'NVARCHAR' },
      { FieldName: 'RCManager',               FieldValue: form.rcManager ?? '',                                                                                                   DataType: 'NVARCHAR' },
      { FieldName: 'RCReviewer',              FieldValue: form.rcReviewer ?? '',                                                                                                  DataType: 'NVARCHAR' },
      { FieldName: 'RCSupervisor',            FieldValue: form.rcSupervisor ?? '',                                                                                                DataType: 'NVARCHAR' },
      { FieldName: 'RCComments',              FieldValue: form.rcCommentsForIssue ?? '',                                                                                          DataType: 'NVARCHAR' },
      { FieldName: 'LocationReviewedImpacted',FieldValue: (form.locationsImpacted ?? []).join(','),                                                                               DataType: 'NVARCHAR' },
      { FieldName: 'LocationNames',           FieldValue: (form.locationsImpacted ?? []).join(','),                                                                               DataType: 'NVARCHAR' },
      { FieldName: 'SubscriberNames',         FieldValue: (form.subscribers ?? []).join(','),                                                                                     DataType: 'NVARCHAR' },
      { FieldName: 'DocumentsJSON',           FieldValue: documents.length ? JSON.stringify(documents) : '[]',                                                                    DataType: 'NVARCHAR' },
    ];

    const actionPlansJson: any[] = [];

    for (const ap of (payload.actionPlans ?? [])) {
      const numericId = String(ap.id ?? '').replace(/^AP-/i, '').trim();
      const orig: any = this.actionPlans.find((a: any) => {
        const rawId = String(a.ActionPlanID ?? a.ActionPlanId ?? a.ID ?? a.Id ?? a.ActionID ?? '').trim();
        return rawId === numericId;
      }) ?? {};
      actionPlansJson.push({
        ActionPlanID:   numericId ? Number(numericId) : null,
        ActionPlanName: ap.id ?? '',
        ReviewID:       orig.ReviewID ?? reviewId,
        Fields: [
          { FieldName: 'AP_Description',        FieldValue: orig.AP_Description ?? orig.Description ?? orig.ActionPlanDescription ?? '',                              DataType: 'NVARCHAR' },
          { FieldName: 'AP_OwnerEmail',          FieldValue: orig.AP_OwnerEmail ?? orig.OwnerEmail ?? '',                                                             DataType: 'NVARCHAR' },
          ...(orig.AP_TargetDate ?? orig.TargetDateForActionPlanRemediationAction ?? orig.TargetDate ? [{ FieldName: 'AP_TargetDate', FieldValue: String(orig.AP_TargetDate ?? orig.TargetDateForActionPlanRemediationAction ?? orig.TargetDate).split('T')[0], DataType: 'DATE' }] : []),
          { FieldName: 'AP_Status',              FieldValue: orig.AP_Status ?? orig.Status ?? orig.ActionPlanStatus ?? '', DataType: 'NVARCHAR' },
          { FieldName: 'AP_InternalDependency',  FieldValue: orig.AP_InternalDependency ?? orig.InternalDependency ?? '',                                             DataType: 'NVARCHAR' },
          { FieldName: 'AP_ClientDependency',    FieldValue: orig.AP_ClientDependency ?? orig.ClientDependency ?? '',                                                 DataType: 'NVARCHAR' },
          ...(orig.AP_ClosedDate ?? orig.ClosedDate ? [{ FieldName: 'AP_ClosedDate', FieldValue: String(orig.AP_ClosedDate ?? orig.ClosedDate).split('T')[0], DataType: 'DATE' }] : []),
          ...(orig.AP_RevisedTargetDate ?? orig.RevisedTargetDate ? [{ FieldName: 'AP_RevisedTargetDate', FieldValue: String(orig.AP_RevisedTargetDate ?? orig.RevisedTargetDate).split('T')[0], DataType: 'DATE' }] : []),
          { FieldName: 'DocumentsJSON',          FieldValue: '[]',                                                                                                   DataType: 'NVARCHAR' },
        ],
      });
    }

    for (let i = 0; i < (payload.newActionPlans ?? []).length; i++) {
      const newAp = payload.newActionPlans[i];
      const newApFiles: any[] = (payload.newActionPlanFiles ?? [])[i] ?? [];
      const newApOwnerEmail = (newAp.owners ?? []).map((o: string) => o.includes('@') ? o : `${o}@accenture.com`).join(';');
      const newApDocuments = await Promise.all(
        newApFiles.map(async (file: File) => {
          const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '') : '';
          const fileContent = await this.readFileAsBase64(file);
          return {
            FileName: file.name,
            StorageFilePath: `${reviewId}/${form.issueId}/${newAp.id}/${file.name}`,
            fileContent, DocumentType: '', FileSize: file.size, FileType: ext, Description: '',
            UploadedBy: this.rebarAuth.getUserName() ?? '',
          };
        }),
      );
      actionPlansJson.push({
        ActionPlanID:   null,
        ActionPlanName: null,
        ReviewID:       reviewId,
        Fields: [
          { FieldName: 'AP_Description',       FieldValue: newAp.description ?? '',                                                                                                                    DataType: 'NVARCHAR' },
          { FieldName: 'AP_OwnerEmail',         FieldValue: newApOwnerEmail,                                                                                                                            DataType: 'NVARCHAR' },
          ...(newAp.targetDate        ? [{ FieldName: 'AP_TargetDate',        FieldValue: newAp.targetDate,        DataType: 'DATE' }] : []),
          { FieldName: 'AP_Status',             FieldValue: newAp.status?.trim() || '',                DataType: 'NVARCHAR' },
          { FieldName: 'AP_InternalDependency', FieldValue: (newAp.internalDependencies ?? []).join(','),                                                                                               DataType: 'NVARCHAR' },
          { FieldName: 'AP_ClientDependency',   FieldValue: newAp.clientDependency ?? '',                                                                                                               DataType: 'NVARCHAR' },
          ...(newAp.closedDate        ? [{ FieldName: 'AP_ClosedDate',        FieldValue: newAp.closedDate,        DataType: 'DATE' }] : []),
          ...(newAp.revisedTargetDate ? [{ FieldName: 'AP_RevisedTargetDate', FieldValue: newAp.revisedTargetDate, DataType: 'DATE' }] : []),
          { FieldName: 'LegacyAPNumber',        FieldValue: newAp.legacyApNumber ?? '',                                                                                                                 DataType: 'NVARCHAR' },
          { FieldName: 'AP_RCComments',         FieldValue: newAp.rcComments ?? '',                                                                                                                    DataType: 'NVARCHAR' },
          ...(newAp.redClientInformedDate        ? [{ FieldName: 'AP_RedClientDate',     FieldValue: newAp.redClientInformedDate,       DataType: 'DATE' }] : []),
          ...(newAp.targetDateToInformRedClient  ? [{ FieldName: 'TargetDateclientinfo', FieldValue: newAp.targetDateToInformRedClient, DataType: 'DATE' }] : []),
          { FieldName: 'DocumentsJSON',         FieldValue: newApDocuments.length ? JSON.stringify(newApDocuments) : '[]',                                                                              DataType: 'NVARCHAR' },
        ],
      });
    }

    const eavParams = {
      category:        'UpsertIssueWithActionPlan_EAV',
      IssueID:         issueIdForUpdate,
      ReviewID:        reviewId,
      ActionPlanID:    null,
      IssueName:       form.issueTitle,
      UserID:          this.rebarAuth.getUserName() ?? '',
      ActionType:      actionType,
      IssueFieldsJSON: JSON.stringify(issueFields),
      ActionPlansJSON: JSON.stringify(actionPlansJson),
    };

    this.reviewerService.UpsertIssueWithActionPlanEAV(eavParams)
      .pipe(timeout(30000), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const formCacheId = String(form.issueId || '').replace(/^[A-Za-z]+-/i, '').trim();
          const responseObj = Array.isArray(response) ? response[0] : response;
          const responseCacheId = String(
            responseObj?.NewIssueID ?? responseObj?.IssueID ?? responseObj?.issueId ?? responseObj?.IssueId ?? responseObj?.data?.IssueID ?? ''
          ).replace(/^[A-Za-z]+-/i, '').trim();
          const subs: string[] = form.subscribers ?? [];
          const existingApIds = (payload.actionPlans ?? []).map((ap: any) => String(ap.id ?? '').replace(/^AP-/i, '').trim()).filter(Boolean);
          const newApIds = (payload.newActionPlans ?? []).map((ap: any) => String(ap.id ?? '').replace(/^AP-/i, '').trim()).filter(Boolean);
          const apIds = [...new Set([...existingApIds, ...newApIds])];
          for (const id of Array.from(new Set([formCacheId, responseCacheId].filter(k => k)))) {
            this.issueSubscriberCache.set(id, [...subs]);
            if (subs.length) { try { localStorage.setItem(`ast_subs_${id}`, JSON.stringify(subs)); } catch {} }
            try { localStorage.setItem(`ast_linked_aps_${id}`, JSON.stringify(apIds)); } catch {}
          }
          this.isLoading = false;
          this.editingReviewId = null;
          this.ngOnInit();
        },
        error: (err) => {
          console.error('UpsertIssueWithActionPlan_EAV error:', err?.status, err?.message);
          this.isLoading = false;
        },
      });
  }

  private loadRepLetterQuestions(): void {
    this.reviewerService.getRepletterQuestions().subscribe({
      next: (response: any) => {
        if (Array.isArray(response)) {
          const questions: string[] = response
            .map((i: any) => i.RepletterQuestions ?? i.RepLetterQuestions ?? i.Name ?? i)
            .filter((v: any) => typeof v === 'string' && v.trim().length > 0);
          if (questions.length) this.repLetterQuestions = questions;
        }
      },
      error: () => {},
    });
  }

  private loadRiskCategories(): void {
    this.reviewerService.getRiskCategories().subscribe({
      next: (response: any) => {
        const raw = Array.isArray(response) ? response[0]?.RiskCategories : response?.RiskCategories;
        try { this.riskCategories = Array.isArray(JSON.parse(raw)) ? JSON.parse(raw) : []; } catch { this.riskCategories = []; }
      },
      error: () => {},
    });
  }

  private loadModalLocations(reviewId: number | null): void {
    if (reviewId == null) return;
    this.reviewerService.getLocationNamesByReviewId(reviewId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (Array.isArray(response)) {
            const locs: string[] = response
              .map((i: any) => i.LocationName ?? i.LocationCity ?? i.Name ?? i)
              .filter((v: any) => typeof v === 'string' && v.trim().length > 0);
            this.locations = locs;
          }
        },
        error: () => {},
      });
  }

  private loadNextIssueId(reviewId: number | null): void {
    this.nextIssueId = '';
    if (reviewId == null) return;
    this.reviewerService.getLastInsertedIssueByReviewId(reviewId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const row = Array.isArray(response) ? response[0] : response;
          let idVal: any = null;
          if (row && typeof row === 'object') {
            idVal = row.IssueID ?? row.IssueId ?? row.LastIssueID ?? row.LastInsertedIssueID ?? Object.values(row)[0];
          } else { idVal = row; }
          const digits = idVal === null || idVal === undefined ? '' : String(idVal).replace(/\D/g, '');
          const lastNum = digits ? parseInt(digits, 10) : 0;
          this.nextIssueId = 'I-' + (lastNum + 1);
        },
        error: () => { this.nextIssueId = ''; },
      });
  }

  private loadNextActionPlanId(reviewId: number | null): void {
    this.nextActionPlanId = '';
    if (reviewId == null) return;
    this.reviewerService.getLastInsertedActionPlanByReviewId(reviewId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const row = Array.isArray(response) ? response[0] : response;
          if (row && typeof row === 'object') {
            const name = row.ActionPlanName ?? row.ActionPlanId ?? row.ActionPlanID;
            if (name != null) {
              const digits = String(name).replace(/\D/g, '');
              this.nextActionPlanId = digits ? 'AP-' + digits : String(name);
            }
          } else if (row != null) {
            const digits = String(row).replace(/\D/g, '');
            this.nextActionPlanId = digits ? 'AP-' + digits : String(row);
          }
        },
        error: () => { this.nextActionPlanId = ''; },
      });
  }

  private loadActionPlans(reviewId: number | null): void {
    this.actionPlans = [];
    if (reviewId == null) return;
    this.reviewerService.getActionPlansEAV(reviewId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const list = Array.isArray(response)
            ? response
            : (response?.actionPlans ?? response?.ActionPlans ?? response?.ActionPlan ?? []);
          this.actionPlans = Array.isArray(list) ? list : [];
        },
        error: () => { this.actionPlans = []; },
      });
  }

  private loadInternalDependencies(): void {
    this.reviewerService.getInternalDependencies().subscribe({
      next: (response: any) => {
        const list = Array.isArray(response) ? response : [];
        const deps: string[] = list
          .map((i: any) => {
            if (typeof i === 'string') return i;
            const val = i.InternalDependencyName ?? i.InternalDependency ?? i.DependencyName ?? i.Dependency ?? i.Name ?? i.Value ?? i.Text;
            if (val !== undefined && val !== null) return String(val);
            return Object.values(i).find((v) => typeof v === 'string' && (v as string).trim().length > 0) as string ?? null;
          })
          .filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
        if (deps.length) this.internalDependencies = deps;
      },
      error: () => {},
    });
  }

  private loadReviewRcPersonnel(reviewId: number | null): void {
    if (reviewId == null) return;
    this.reviewerService.getReviewDetailsByID(reviewId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const rows: any[] = Array.isArray(response)
            ? response
            : (response?.value ?? response?.recordset ?? [response]);
          const d = rows[0] ?? {};
          this.reviewRcReviewer  = d.RCReviewer  ?? '';
          this.reviewRcSupervisor = d.RCSupervisor ?? '';
          this.reviewRcManager   = d.RCManager   ?? '';
          this.reviewSecondaryRcReviewer  = d.SecondaryReviewer  ?? '';
          this.reviewSecondaryRcSupervisor = d.SecondarySupervisor ?? '';
          this.reviewSecondaryRcManager   = d.SecondaryManager   ?? '';
        },
        error: () => {},
      });
  }



  private toDisplayDate(iso: string): string {
    if (!iso) return '';
    const datePart = iso.split('T')[0];
    const parts = datePart.split('-').map(Number);
    if (parts.length < 3 || !parts[0] || !parts[1] || !parts[2]) return '';
    const [y, m, d] = parts;
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d).padStart(2, '0')}-${MONTHS[m - 1]}-${y}`;
  }

  private toFinancialYearId(fy: string): string | null {
    if (!fy) return null;
    const digits = fy.replace(/\D/g, '');
    return digits ? '20' + digits : null;
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const idx = result.indexOf(',');
        resolve(idx >= 0 ? result.substring(idx + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: any) {
    const clickedInsideTable = event.target.closest('table');
    const clickedInsideFilter = event.target.closest('.filter-dropdown');
    if (!clickedInsideTable && !clickedInsideFilter) {
      this.activeFilterColumn = null;
    }
  }

  setTooltipIfTruncated(event: MouseEvent, text: string): void {
    const td = event.currentTarget as HTMLElement;
    const span = td.querySelector('.truncate-text') as HTMLElement;
    if (span && span.scrollWidth > span.clientWidth) {
      td.setAttribute('data-tip', text ?? '');
    } else {
      td.removeAttribute('data-tip');
    }
  }

  displayIssueId(id: any): string {
    if (id === null || id === undefined || id === '') return '';
    const s = String(id).trim();
    return /^i-/i.test(s) ? s : `I-${s}`;
  }

  displayLinkedActionPlan(ap: any): string {
    if (!ap) return '';
    const s = String(ap).trim();
    if (!s) return '';
    return s.split(',').map(p => {
      const t = p.trim();
      return t && !/^ap-/i.test(t) ? `AP-${t}` : t;
    }).filter(Boolean).join(', ');
  }
}
